import clsx from 'clsx'
import isEmpty from 'lodash/isEmpty'
import React, { FC, useContext, useEffect, useRef, useState } from 'react'

import { BraintreeContext } from '@Domains/Checkout/Providers/BraintreeContextProvider'
import { CheckoutFlowContext } from '@Domains/CheckoutV2/Features/CheckoutSteps/Contexts/CheckoutFlowContext'
import { CheckoutFlowStep, CheckoutFlowSteps } from '@Domains/CheckoutV2/Features/CheckoutSteps/Contexts/typings/CheckoutFlowTypes'
import {
  useBraintreeCheckoutResponse,
} from '@Domains/CheckoutV2/Features/CheckoutSteps/Steps/CheckoutPaymentStep/Components/ProviderComponents/Braintree/Hooks/useBraintreeCheckoutResponse'
import { CheckoutPaymentComponentProps } from '@Domains/CheckoutV2/Features/CheckoutSteps/Steps/CheckoutPaymentStep/Hooks/usePaymentComponentFactory'

import styles from './BraintreePaypal.module.scss'

interface PaypalInstanceProps {
  createPayment: Function
  loadPayPalSDK: Function
  tokenizePayment: Function
}

interface PaypalProps {
  Buttons: Function
  FUNDING: Record<string, string>
  getCorrelationID: Function
  version: string
}

// see https://developer.paypal.com/braintree/docs/guides/paypal/checkout-with-paypal/javascript/v3/
export const BraintreePaypalButton: FC<CheckoutPaymentComponentProps> = ({
  grandTotal,
  option,
  submitPayment,
}) => {
  const ref = useRef<HTMLDivElement | null>(null)
  const {
    setActiveStep,
    stepValidations,
  } = useContext(CheckoutFlowContext)

  const {
    braintreeClient,
    braintreeInstance,
  } = useContext(BraintreeContext)
  const [mounted, setMounted] = useState(false)
  const [paypalInstance, setPaypalInstance] = useState<PaypalInstanceProps>()
  const { handleBraintreePaymentSubmitResponse } = useBraintreeCheckoutResponse()

  useEffect(() => {
    if (!mounted && !!braintreeInstance?.paypalCheckout && !!ref.current && !!braintreeClient) {
      setMounted(true)
      braintreeInstance.paypalCheckout.create({
        client: braintreeClient,
      }, (paypalCheckoutErr, paypalCheckoutInstance: PaypalInstanceProps) => {
        if (paypalCheckoutErr) {
          console.warn('PayPal error creating Braintree PayPal Checkout:', paypalCheckoutErr)
          return
        }
        paypalCheckoutInstance.loadPayPalSDK({
          vault: false,
          'disable-funding': 'credit,card',
          intent: 'authorize',
        }, (loadSDKErr) => {
          if (loadSDKErr) {
            console.warn('PayPal error loading Braintree PayPal SDK:', loadSDKErr)
            return
          }
          setPaypalInstance(paypalCheckoutInstance)
        })
      })
    }
  }, [braintreeInstance, mounted, braintreeClient, grandTotal, handleBraintreePaymentSubmitResponse, option.type])

  useEffect(() => {
    if (paypalInstance && isEmpty(ref.current?.children)) {
      const paypal: PaypalProps | null = (typeof window !== 'undefined' && (window as any).paypal ? (window as any).paypal : null)
      if (!paypal) {
        return
      }
      paypal.Buttons({
        fundingSource: paypal.FUNDING.PAYPAL,
        createOrder: () => paypalInstance.createPayment({
          flow: 'checkout',
          intent: 'authorize',
          displayName: 'Trendhim',
          amount: grandTotal?.value?.plain,
          currency: 'USD',
        }),
        onApprove: data => paypalInstance.tokenizePayment(data, (err, payload) => {
          if (err) {
            console.warn('PayPal Error Approving Braintree PayPal:', err)
            return
          }
          submitPayment({
            formData: payload,
            isValid: true,
            type: option.type,
          }, response => handleBraintreePaymentSubmitResponse(option.type, response))
        }),

        onCancel: (data) => {
          console.warn('PayPal payment cancelled', JSON.stringify(data, null, 2))
        },
        onError: (err) => {
          console.error('PayPal error', err)
        },
      })
        .render(ref.current)
    }
  }, [option.id, paypalInstance, option.type, handleBraintreePaymentSubmitResponse, grandTotal, submitPayment, stepValidations])

  return (
    <div className={styles.braintreePaypal}>
      {!(stepValidations[CheckoutFlowStep.ADDRESS] && stepValidations[CheckoutFlowStep.SHIPPING])
        && <div className={styles.buttonDisableLayer} onClick={() => setActiveStep(CheckoutFlowSteps.SUCCESS)} />}
      <div
        key={option.id}
        aria-disabled
        className={clsx(
          styles.braintreePayPalButton,
          !(stepValidations[CheckoutFlowStep.ADDRESS] && stepValidations[CheckoutFlowStep.SHIPPING]) && styles.disabled,
        )}
        ref={ref}
      />
    </div>
  )
}
